import React, { useCallback, memo } from 'react';
import useLocale from '@/hooks/useLocale';
import * as Styles from './PlanDetails.styles';
import { useHistory } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import { Button, AlertV2 } from 'gatorcomponents';
import { formatTimestampDate } from '@/utils/Formatters/formatTimestampDate';

import { VPS_OCI_PAGE_STEPS } from '@/pages/Products/VpsOciDetailPage/VpsOciDetailPage.enum';

import ContentSection from '@/pages/Products/VpsOciDetailPage/commons/ContentSection';

const PlanDetails = ({
  onNavigateToStep,
  disableUpgradeButton = false,
}) => {
  const history = useHistory();
  const details = useSelector(state => state.vpsOci?.data?.details, shallowEqual);
  const {
    billing: billingLocale,
    productDetails: productDetailsLocale,
    routes: routesLocale,
  } = useLocale();

  const {
    paymentMethods: paymentMethodsLocale,
    paymentCycle: paymentCycleLocale,
  } = billingLocale;

  const {
    vpsOci: vpsOciLocale,
  } = productDetailsLocale;

  const allowUpgrade = details && details.allowUpgrade;
  const registerDateValue = (details && details.registerDate) && formatTimestampDate(details.registerDate);
  const nextDueDateValue = (details && details.nextDueDate) && formatTimestampDate(details.nextDueDate);
  const billingCycle = (details && details.billingCycle && paymentCycleLocale) && paymentCycleLocale[details.billingCycle];
  const paymentMethod = (details && details.paymentMethod && paymentMethodsLocale) && paymentMethodsLocale[details.paymentMethod];

  const handleOnStopCancelClick = useCallback(() => {
    history.push(routesLocale.invoices);
  }, [history, routesLocale.invoices]);

  const handleShowUpgradePage = useCallback(() => {
    onNavigateToStep && onNavigateToStep(VPS_OCI_PAGE_STEPS.UPGRADE_LISTING);
  }, [onNavigateToStep]);

  if (!details || Object.keys(details).length === 0) return null;

  return (
    <>
      <ContentSection
        direction="column"
        align="flex-start"
        justify="flex-start"
        title={vpsOciLocale.planDetails.title}
        testId="vpsoci-myplan-section"
      >
        <Styles.ContentWrapper>
          <Styles.DataBlocksWrapper>
            {registerDateValue && (
              <Styles.DataBlock data-testid="vpsoci-myplan-register-date-item">
                <Styles.DataLabel>{vpsOciLocale.planDetails.registerDate}</Styles.DataLabel>
                <Styles.DataValue data-testid="vpsoci-myplan-register-date-value">
                  {registerDateValue}
                </Styles.DataValue>
              </Styles.DataBlock>
            )}
            {nextDueDateValue && (
              <Styles.DataBlock data-testid="vpsoci-myplan-next-due-date-item">
                <Styles.DataLabel>{vpsOciLocale.planDetails.nextDueDate}</Styles.DataLabel>
                <Styles.DataValue data-testid="vpsoci-myplan-next-due-date-value">
                  {nextDueDateValue}
                </Styles.DataValue>
              </Styles.DataBlock>
            )}
            {billingCycle && (
              <Styles.DataBlock data-testid="vpsoci-myplan-billing-cycle-item">
                <Styles.DataLabel>{vpsOciLocale.planDetails.billingCycle}</Styles.DataLabel>
                <Styles.DataValue data-testid="vpsoci-myplan-billing-cycle-value">{billingCycle}</Styles.DataValue>
              </Styles.DataBlock>
            )}
            {paymentMethod && (
              <Styles.DataBlock data-testid="vpsoci-myplan-payment-method-item">
                <Styles.DataLabel>{vpsOciLocale.planDetails.paymentMethod}</Styles.DataLabel>
                <Styles.DataValue data-testid="vpsoci-myplan-payment-method-value">{paymentMethod}</Styles.DataValue>
              </Styles.DataBlock>
            )}
          </Styles.DataBlocksWrapper>

          {allowUpgrade && (
            <Styles.ButtonWrapper>
              <Button
                label={vpsOciLocale.planDetails.upgrade.button}
                variant="secondary"
                size="large"
                version="v2"
                width="full"
                onClick={handleShowUpgradePage}
                disabled={disableUpgradeButton}
                testId="vpsoci-myplan-upgrade-button"
              />
            </Styles.ButtonWrapper>
          )}
        </Styles.ContentWrapper>
      </ContentSection>
      {disableUpgradeButton && (
        <AlertV2
          onPrimaryAction={handleOnStopCancelClick}
          primaryButtonLabel={vpsOciLocale.planDetails.upgrade.alert.action}
          title={vpsOciLocale.planDetails.upgrade.alert.title}
          testId="alert"
          variant="info"
          width="full"
          withoutBackground={false}
          description={vpsOciLocale.planDetails.upgrade.alert.description}
        />
      )}
    </>
  );
};

export default memo(PlanDetails);
